<template>
  <div class="chat-page">

    <!-- ── Header ─────────────────────────────────────────────── -->
    <div class="chat-header">
      <button class="back-btn" @click="$router.push('/support_v2/list')">
        <i class="fa fa-arrow-left"></i>
      </button>

      <div class="header-info">
        <div class="user-avatar">{{ (name || 'U').charAt(0).toUpperCase() }}</div>
        <div>
          <div class="event-title">{{ name || 'Unknown User' }}</div>
          <div class="event-sub">{{ phone }} · Support Ticket</div>
        </div>
      </div>

      <div class="header-right">
        <span class="conn-badge" :class="socketConnected ? 'conn-live' : 'conn-off'">
          <span class="conn-dot"></span>
          {{ socketConnected ? 'Live' : 'Offline' }}
        </span>
        <button v-if="isActive" class="close-ticket-btn" @click="closeTicket">
          <i class="fa fa-times-circle"></i> Close Ticket
        </button>
        <span v-else class="closed-badge">✓ Closed</span>
      </div>
    </div>

    <!-- ── Chat body ──────────────────────────────────────────── -->
    <div class="chat-body" ref="chatContainer">

      <!-- Empty state -->
      <div v-if="messages.length === 0 && !loading" class="empty-state">
        <div class="empty-icon">🎧</div>
        <p>No messages yet.</p>
        <span>Support conversation will appear here.</span>
      </div>

      <!-- Messages: flat list interleaves divider + bubble items -->
      <div
        v-for="item in flatMessages"
        :key="item._key"
        :class="item._type === 'divider'
          ? 'date-divider'
          : ['msg-row', item._isAdmin ? 'row-me' : 'row-them']"
      >
        <!-- DATE DIVIDER -->
        <span v-if="item._type === 'divider'">{{ formatDivider(item.createdAt) }}</span>

        <!-- BUBBLE -->
        <template v-else>
          <div v-if="!item._isAdmin" class="avatar">
            {{ (name || 'U').charAt(0).toUpperCase() }}
          </div>
          <div class="bubble-wrap">
            <div class="sender-name">
              <span v-if="item._isAdmin" class="role-chip admin-chip">Admin</span>
              <span class="sender-text-name">{{ item._isAdmin ? (item.sender && (item.sender.displayname || item.sender.username) ? (item.sender.displayname || item.sender.username) : 'Admin') : (name || 'User') }}</span>
              <span v-if="!item._isAdmin" class="role-chip user-chip">User</span>
            </div>
            <div class="bubble" :class="item._isAdmin ? 'bubble-me' : 'bubble-them'">
              <span class="bubble-text">{{ item.content }}</span>
              <span class="bubble-time">{{ formatTime(item.createdAt) }}</span>
            </div>
          </div>
          <div v-if="item._isAdmin" class="avatar avatar-me">
            <i class="fa fa-user-shield"></i>
          </div>
        </template>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="body-loading">
        <div class="spinner"></div>
      </div>
    </div>

    <!-- ── Typing indicator (placeholder for future use) ─────── -->
    <div class="typing-bar" style="min-height:4px;"></div>

    <!-- ── Input bar ──────────────────────────────────────────── -->
    <div class="input-bar">
      <div v-if="!isActive" class="ticket-closed-msg">
        <i class="fa fa-lock"></i> This ticket is closed. No new messages can be sent.
      </div>
      <div v-else class="input-wrap">
        <input
          ref="msgInput"
          class="msg-input"
          placeholder="Type your reply…"
          v-model="newMessage"
          @keydown.enter.prevent="sendMessage"
          :disabled="!socketConnected"
        />
        <button
          class="send-btn"
          @click="sendMessage"
          :disabled="sending || !newMessage.trim() || !socketConnected"
        >
          <i class="fa fa-paper-plane"></i>
        </button>
      </div>
    </div>

  </div>
</template>

<script>
import moment from "moment";
import { io } from "socket.io-client";

export default {
  data() {
    return {
      loading: false,
      sending: false,
      chatId: null,
      name: "Unknown",
      phone: "N/A",
      messages: [],
      newMessage: "",
      isActive: true,
      socket: null,
      socketConnected: false,
      userId: null,
      adminId: null,
    };
  },

  computed: {
    flatMessages() {
      const items = [];
      this.messages.forEach((msg, idx) => {
        const prev = this.messages[idx - 1];
        if (idx === 0 || this.isDifferentDay(prev, msg)) {
          items.push({ _type: 'divider', _key: 'd-' + idx, createdAt: msg.createdAt });
        }
        items.push(Object.assign({}, msg, { _type: 'msg', _key: 'b-' + (msg._id || idx) }));
      });
      return items;
    },
  },

  async mounted() {
    this.chatId = this.$route.query.id;
    if (this.chatId) {
      this.loading = true;
      this.initSocket();
    }
  },

  beforeDestroy() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  },

  methods: {
    // ── Helpers ───────────────────────────────────────────────
    isSameId(a, b) {
      if (!a || !b) return false;
      const strA = typeof a === 'object' ? (a._id || a).toString() : a.toString();
      const strB = typeof b === 'object' ? (b._id || b).toString() : b.toString();
      return strA === strB;
    },

    tagMessage(msg) {
      const isAdmin =
        msg.senderRole === 'admin' ||
        this.isSameId(msg.sender, this.adminId) ||
        this.isSameId(msg.sender, window.localStorage.getItem("userid"));
      return { ...msg, _isAdmin: isAdmin };
    },

    formatTime(d) {
      if (!d) return '';
      const now = moment();
      const m   = moment(d);
      const diffSec = now.diff(m, 'seconds');
      const diffMin = now.diff(m, 'minutes');
      const diffDay = now.diff(m, 'days');

      if (diffSec < 60)              return 'Just now';
      if (diffMin < 60)              return diffMin + 'm ago';
      if (m.isSame(now, 'day'))      return m.format('h:mm A');           // 2:30 PM
      if (diffDay === 1)             return 'Yesterday ' + m.format('h:mm A');
      if (diffDay < 7)               return m.format('ddd h:mm A');        // Mon 2:30 PM
      if (m.isSame(now, 'year'))     return m.format('MMM D, h:mm A');    // Jun 4, 2:30 PM
      return m.format('MMM D YYYY, h:mm A');
    },

    formatDivider(d) {
      if (!d) return '';
      const now = moment().startOf('day');
      const m   = moment(d).startOf('day');
      const diffDay = now.diff(m, 'days');
      
      if (m.isSame(now, 'day'))        return 'Today';
      if (diffDay === 1)               return 'Yesterday';
      if (diffDay < 7)                 return m.format('dddd');         // Monday, Tuesday…
      if (diffDay >= 7 && diffDay < 14) return 'Last Week';
      if (diffDay >= 14 && diffDay < 30) return Math.floor(diffDay / 7) + ' weeks ago';
      if (diffDay >= 30 && diffDay < 60) return 'Last Month';
      if (m.isSame(now, 'year'))       return m.format('MMMM D');       // June 4
      return m.format('MMMM D, YYYY');
    },

    isDifferentDay(prev, cur) {
      if (!prev || !cur) return false;
      return this.formatDivider(prev.createdAt) !== this.formatDivider(cur.createdAt);
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const c = this.$refs.chatContainer;
        if (c) c.scrollTop = c.scrollHeight;
      });
    },

    // ── Socket ────────────────────────────────────────────────
    initSocket() {
      const baseUrl = this.$apiService.apiUrl.replace(/\/api\/?$/, '').replace(/\/$/, '');
      const token   = window.localStorage.getItem("token");

      this.socket = io(`${baseUrl}/chat`, {
        auth: { token: `Bearer ${token}` },
        reconnection: true,
        reconnectionAttempts: 10,
        reconnectionDelay: 1000,
        forceNew: true
      });

      const onConnect = () => {
        this.socketConnected = true;
        console.log('[Socket] Connected, requesting history for', this.chatId);
        this.socket.emit('chat:history', { chatId: this.chatId });
      };

      this.socket.on('connect', onConnect);
      if (this.socket.connected) {
        onConnect();
      }

      this.socket.on('disconnect', (reason) => {
        this.socketConnected = false;
        console.log('[Socket] Disconnected:', reason);
        this.loading = false;
      });

      this.socket.on('connect_error', (err) => {
        this.socketConnected = false;
        console.error('[Socket] Connection error:', err);
        this.loading = false;
      });

      this.socket.on('error', (err) => {
        console.error('[Socket] Error:', err);
        this.loading = false;
      });

      this.socket.on('chat:history', (payload) => {
        console.log('[Socket] Received chat:history payload:', payload);
        const chat = payload.chat;
        if (chat) {
          const u = chat.userId;
          this.name    = u ? (((u.first_name || '') + ' ' + (u.last_name || '')).trim() || 'No Name') : 'Unknown';
          this.phone   = (u && u.phone_number) ? u.phone_number : 'N/A';
          this.isActive = chat.isActive !== false;
          this.userId  = u ? u._id.toString() : null;
          this.adminId = chat.adminId ? (chat.adminId._id || chat.adminId).toString() : window.localStorage.getItem("userid");
          this.messages = (chat.messages || []).map(m => this.tagMessage(m));
          this.scrollToBottom();
          this.joinRoom();

          // Auto-read existing unread messages sent by the user
          const unreadIds = (chat.messages || [])
            .filter(msg => {
              const isUserMsg = msg.senderRole === 'user' || msg.senderModel === 'user';
              if (!isUserMsg) return false;
              const hasRead = msg.readBy && msg.readBy.length > 0;
              return !hasRead;
            })
            .map(msg => msg._id);

          if (unreadIds.length > 0) {
            console.log('[Socket] Auto-reading unread messages:', unreadIds);
            this.socket.emit('chat:read', { chatId: this.chatId, messageIds: unreadIds });
          }
        } else {
          console.warn('[Socket] No chat found in chat:history payload:', payload.error || 'Unknown error');
        }
        this.loading = false;
      });

      this.socket.on('chat:receive', (rawMsg) => {
        // If message is already in the list, update it (useful for read receipts)
        if (rawMsg._id && this.messages.some(m => m._id && m._id.toString() === rawMsg._id.toString())) {
          const idx = this.messages.findIndex(m => m._id && m._id.toString() === rawMsg._id.toString());
          if (idx !== -1) this.$set(this.messages, idx, this.tagMessage(rawMsg));
          return;
        }

        const isFromMe = this.isSameId(rawMsg.sender, this.adminId);
        
        if (isFromMe) {
          // Find the optimistic message that has no ID and replace it
          const optIdx = this.messages.findIndex(m => m._id === null && m._optimistic);
          if (optIdx !== -1) {
            this.$set(this.messages, optIdx, this.tagMessage(rawMsg));
          }
        } else {
          // Push messages from the other user
          this.messages.push(this.tagMessage(rawMsg));
          this.scrollToBottom();

          // Auto-read incoming messages immediately
          if (rawMsg._id) {
            this.socket.emit('chat:read', { chatId: this.chatId, messageIds: [rawMsg._id] });
          }
        }
      });
    },

    joinRoom() {
      if (this.socket && this.userId) {
        this.socket.emit('chat:join', { adminId: this.userId });
      }
    },

    // ── Send ──────────────────────────────────────────────────
    async sendMessage() {
      const content = this.newMessage.trim();
      if (!content || !this.isActive || !this.socket || !this.socketConnected) return;
      this.sending = true;
      const optimisticMsg = {
        _id: null, _optimistic: true, _isAdmin: true,
        sender: this.adminId, senderRole: 'admin',
        content, type: 'text',
        createdAt: new Date().toISOString(),
      };
      this.messages.push(optimisticMsg);
      this.newMessage = "";
      this.scrollToBottom();
      this.socket.emit('chat:send', { adminId: this.userId, content, type: 'text', ticket: 'support_ticket' });
      this.sending = false;
      this.$nextTick(() => this.$refs.msgInput && this.$refs.msgInput.focus());
    },

    // ── Close ticket ──────────────────────────────────────────
    async closeTicket() {
      if (!confirm("Are you sure you want to close this ticket?")) return;
      this.loading = true;
      const res = await this.$apiService.patchCall("support-v2/admin/tickets/" + this.chatId + "/close");
      if (!res.isError) {
        this.isActive = false;
        this.$apiService.notify("Ticket closed successfully");
      } else {
        this.$apiService.notify("Failed to close ticket");
      }
      this.loading = false;
    },
  },
};
</script>

<style scoped>
/* ── Page shell ──────────────────────────────────────────────── */
.chat-page {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 180px);
  min-height: 450px;
  background: #0d1117;
  border-radius: 16px;
  overflow: hidden;
  margin: 0 16px 16px;
  box-shadow: 0 8px 40px rgba(0,0,0,0.6);
}

/* ── Header ──────────────────────────────────────────────────── */
.chat-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 20px;
  background: linear-gradient(135deg, #1a1f2e 0%, #0d1117 100%);
  border-bottom: 1px solid rgba(255,255,255,0.07);
  flex-shrink: 0;
}
.back-btn {
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.1);
  color: #e2e8f0;
  width: 36px; height: 36px;
  border-radius: 10px;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: background 0.2s;
  flex-shrink: 0;
}
.back-btn:hover { background: rgba(255,255,255,0.12); }

.header-info {
  display: flex; align-items: center; gap: 12px; flex: 1;
}
.user-avatar {
  width: 42px; height: 42px; border-radius: 50%;
  background: linear-gradient(135deg, #1d8cf8, #3358f4);
  display: flex; align-items: center; justify-content: center;
  font-weight: 700; font-size: 18px; color: white;
  flex-shrink: 0;
}
.event-title { font-size: 16px; font-weight: 700; color: #f1f5f9; }
.event-sub   { font-size: 11px; color: #64748b; margin-top: 1px; }

.header-right {
  display: flex; align-items: center; gap: 10px; flex-shrink: 0;
}
.conn-badge {
  display: flex; align-items: center; gap: 6px;
  padding: 4px 12px; border-radius: 20px;
  font-size: 12px; font-weight: 600;
}
.conn-live { background: rgba(0,242,195,0.12); color: #00f2c3; border: 1px solid rgba(0,242,195,0.3); }
.conn-off  { background: rgba(253,93,147,0.12); color: #fd5d93; border: 1px solid rgba(253,93,147,0.3); }
.conn-dot  { width: 6px; height: 6px; border-radius: 50%; background: currentColor; animation: pulse 1.5s infinite; }
@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }

.close-ticket-btn {
  background: rgba(253,93,147,0.12);
  border: 1px solid rgba(253,93,147,0.35);
  color: #fd5d93;
  padding: 5px 14px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
  display: flex; align-items: center; gap: 6px;
}
.close-ticket-btn:hover { background: rgba(253,93,147,0.22); }
.closed-badge {
  font-size: 12px; color: #64748b;
  background: rgba(255,255,255,0.05);
  padding: 4px 10px; border-radius: 8px;
}

/* ── Chat body ───────────────────────────────────────────────── */
.chat-body {
  flex: 1; overflow-y: auto;
  padding: 20px 20px 8px;
  display: flex; flex-direction: column; gap: 2px;
  scroll-behavior: smooth;
}
.chat-body::-webkit-scrollbar { width: 4px; }
.chat-body::-webkit-scrollbar-track { background: transparent; }
.chat-body::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 4px; }

.empty-state {
  flex: 1; display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  color: #475569; text-align: center; padding: 40px;
}
.empty-icon { font-size: 48px; margin-bottom: 12px; }
.empty-state p    { font-size: 16px; color: #64748b; margin: 0 0 6px; }
.empty-state span { font-size: 13px; color: #334155; }

.date-divider { text-align: center; margin: 16px 0 8px; }
.date-divider span {
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.08);
  color: #64748b; font-size: 11px;
  padding: 3px 12px; border-radius: 20px;
}

.msg-row {
  display: flex; align-items: flex-end; gap: 8px;
  margin-bottom: 6px; animation: fadeUp 0.2s ease;
}
@keyframes fadeUp { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
.row-me   { flex-direction: row-reverse; }
.row-them { flex-direction: row; }

.avatar {
  width: 32px; height: 32px; border-radius: 50%;
  background: linear-gradient(135deg, #1d8cf8, #3358f4);
  display: flex; align-items: center; justify-content: center;
  font-size: 12px; font-weight: 700; color: white; flex-shrink: 0;
}
.avatar-me {
  background: linear-gradient(135deg, #e14eca, #a855f7);
  font-size: 13px;
}

.bubble-wrap { display: flex; flex-direction: column; max-width: 68%; }
.row-me .bubble-wrap { align-items: flex-end; }

.sender-name {
  font-size: 11px; font-weight: 600; color: #94a3b8;
  margin-bottom: 3px; padding-left: 4px;
  display: flex; align-items: center; gap: 5px;
}
.role-chip {
  font-size: 9px; font-weight: 700; padding: 1px 6px;
  border-radius: 8px; letter-spacing: 0.4px; text-transform: uppercase;
}
.user-chip  { background: rgba(29,140,248,0.2); color: #1d8cf8; }
.admin-chip { background: rgba(225,78,202,0.2); color: #e14eca; }

.bubble {
  padding: 10px 14px; border-radius: 16px;
  line-height: 1.5; display: flex; flex-direction: column;
  gap: 4px; word-break: break-word;
}
.bubble-me {
  background: linear-gradient(135deg, #e14eca 0%, #a855f7 100%);
  color: white; border-bottom-right-radius: 4px;
  box-shadow: 0 4px 16px rgba(225,78,202,0.25);
}
.bubble-them {
  background: linear-gradient(135deg, #1e293b 0%, #0f1a2e 100%);
  color: #e2e8f0; border: 1px solid rgba(255,255,255,0.07);
  border-bottom-left-radius: 4px;
}
.bubble-text { font-size: 14px; }
.bubble-time { font-size: 10px; opacity: 0.6; text-align: right; align-self: flex-end; white-space: nowrap; }

.body-loading {
  display: flex; align-items: center; justify-content: center;
  flex: 1; padding: 40px;
}
.spinner {
  width: 32px; height: 32px;
  border: 3px solid rgba(255,255,255,0.08);
  border-top-color: #e14eca;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ── Input bar ───────────────────────────────────────────────── */
.input-bar {
  padding: 12px 16px;
  background: rgba(15,23,42,0.9);
  border-top: 1px solid rgba(255,255,255,0.06);
  flex-shrink: 0;
}
.ticket-closed-msg {
  text-align: center; color: #475569; font-size: 13px;
  padding: 10px; background: rgba(255,255,255,0.03);
  border-radius: 10px; border: 1px solid rgba(255,255,255,0.06);
}
.input-wrap {
  display: flex; align-items: center; gap: 10px;
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 14px; padding: 6px 6px 6px 16px;
  transition: border-color 0.2s;
}
.input-wrap:focus-within {
  border-color: rgba(225,78,202,0.5);
  box-shadow: 0 0 0 3px rgba(225,78,202,0.08);
}
.msg-input {
  flex: 1; background: transparent; border: none; outline: none;
  color: #e2e8f0; font-size: 14px;
}
.msg-input::placeholder { color: #475569; }
.msg-input:disabled { opacity: 0.4; cursor: not-allowed; }
.send-btn {
  width: 38px; height: 38px; border-radius: 10px;
  background: linear-gradient(135deg, #e14eca, #a855f7);
  border: none; color: white;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; font-size: 14px;
  transition: opacity 0.2s, transform 0.1s; flex-shrink: 0;
}
.send-btn:hover:not(:disabled) { opacity: 0.9; transform: scale(1.05); }
.send-btn:disabled { opacity: 0.35; cursor: not-allowed; }
</style>
